import React from "react";
import { Document, Page } from "react-pdf";
import scoreIcon from "../img/analytics.svg";
import downloadIcon from "../img/download_icon.svg";
import deleteIcon from "../img/delete_icon.svg";
import { useAuthToken } from "../AuthProvider";
import { toast } from "react-toastify";
import { User, UserScore } from "../Profile";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppState } from "../store";

/* eslint-disable @typescript-eslint/ban-types */
interface ResumeCardProps {
  width: number;
  data: any;
  filename: string;
  uuid: string;
  decrementFunc: () => void;
  showScore: (uuid: string) => void;
}
/* eslint-enable @typescript-eslint/ban-types */

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const ResumeCard: React.FC<ResumeCardProps> = ({
  width,
  data,
  filename,
  uuid,
  decrementFunc,
  showScore,
}) => {
  const { isAuthenticated, token, updateToken } = useAuthToken();
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const [isHighlightOn, setIsHighlightOn] = React.useState(false);
  const [barHighlight, setBarHighlight] = React.useState(false);
  const userInfo = useSelector((state: AppState) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pending: boolean = uuid == "blank.pdf";

  const updateUserResume = (uuid: string) => {
    const updatedUser: User = {
      ...userInfo, // create a new object with all existing properties of the user object
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function deleteResume() {
    const apiEndpoint =  "/api/resume?file=" + uuid;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      await fetch(apiEndpoint, {
        method: "DELETE",
        headers: headers,
      });

      decrementFunc();
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }

    setIsOverlayOpen(false);
  }

  const pdfData = React.useMemo(() => {
    return { data: atob(data) };
  }, [data]);

  async function downloadPDF() {
    const apiEndpoint =  "/api/download?file=" + uuid;

    const headers = {
      "Content-Disposition": "attachment",
      Authorization: `Bearer ${token}`,
    };

    try {
      await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        });
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  return (
    <div className="light-panel relative w-full rounded-xl">
      <div className={`z-10 flex flex-col items-center rounded-xl`}>
        <Document file={pdfData} options={options}>
          <Page
            pageNumber={1}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={width}
            className={"relative rounded-t-xl pt-4"}
            onMouseEnter={() => setIsHighlightOn(true)}
            onMouseLeave={() => setIsHighlightOn(false)}
          >
            <button
              className={`${pending ? "hidden" : "block"}`}
              onClick={() => {
                updateUserResume(uuid);
                navigate("/score");
              }}
            >
              <div
                className={`absolute top-0 z-20 h-full w-full rounded-t-xl bg-black opacity-60 ${
                  isHighlightOn && !barHighlight ? "visible" : "invisible"
                }`}
              />
            </button>

            <div
              className="absolute z-30 w-full rounded-b-xl bg-white"
              onMouseEnter={() => setBarHighlight(true)}
              onMouseLeave={() => setBarHighlight(false)}
            >
              <div>
                {pending && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-b-xl bg-white bg-opacity-75">
                    <svg
                      aria-hidden="true"
                      className="inline h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                )}

                <div
                  className={`flex flex-row items-center justify-between px-2 pb-2`}
                >
                  <p
                    className={`overflow-hidden w-[${width}] text-nowrap hidden whitespace-nowrap font-code text-xs font-bold lg:block`}
                  >
                    {filename.substring(0, width / 12)}
                    {filename.length <= width / 12 ? "" : "..."}
                  </p>
                  <div
                    className={`flex flex-row ${
                      pending ? "collapse" : "visible"
                    }`}
                  >
                    <button onClick={() => showScore(uuid)}>
                      <img className="pr-2" src={scoreIcon} />
                    </button>
                    <button onClick={downloadPDF}>
                      <img className="pr-2" src={downloadIcon}></img>
                    </button>
                    <button onClick={() => setIsOverlayOpen(true)}>
                      <img src={deleteIcon}></img>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Page>
        </Document>

        {isOverlayOpen && (
          <div className="overlay">
            <div className="agreement-content w-1/2">
              <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
                Are you sure you want to delete{" "}
                <span className="font-bold">{filename}</span>? This cannot be
                undone!
              </h2>

              <div className="flex w-full flex-row pt-4 lg:pt-6">
                <button
                  className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                  onClick={() => deleteResume()}
                >
                  Yes, I&apos;m sure
                </button>
                <button
                  className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                  onClick={() => setIsOverlayOpen(false)}
                >
                  Nevermind
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResumeCard;
